import React from 'react';
import { Check, X, Images, BookImage, Calendar } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Avatar } from '../UI/Avatar';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { PERMISSION_LABELS } from '../../api/eventTeam';
import type { AppNotification, EventManagerPermissions } from '../../types';
import type { InvitePreviewEvent } from '../../api/notifications';
import styles from './ManagerInviteModal.module.css';

interface ManagerInviteModalProps {
  isOpen: boolean;
  notification: AppNotification;
  preview: InvitePreviewEvent | null;
  previewLoading: boolean;
  /** Which action is in flight, so we can show a spinner on the right button. */
  actioning: 'accept' | 'decline' | null;
  onAccept: () => void;
  onDecline: () => void;
  onSkip: () => void;
}

function grantedLabels(perms: EventManagerPermissions): string[] {
  return (Object.entries(perms) as [keyof EventManagerPermissions, boolean][])
    .filter(([, v]) => v)
    .map(([k]) => PERMISSION_LABELS[k]);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ManagerInviteModal({
  isOpen,
  notification,
  preview,
  previewLoading,
  actioning,
  onAccept,
  onDecline,
  onSkip,
}: ManagerInviteModalProps) {
  const { data } = notification;
  const granted = grantedLabels(data.permissions);
  const isFull = granted.length >= 8;
  const visible = isFull ? [] : granted.slice(0, 3);
  const overflow = isFull ? 0 : granted.length - visible.length;
  const busy = actioning !== null;
  const eventName = preview?.name ?? data.eventName;

  return (
    <Modal isOpen={isOpen} onClose={onSkip} size="md">
      <div className={styles['wrap']}>
        {/* ── Event preview banner with overlapping mascot ──────────────── */}
        <div className={styles['banner']}>
          {preview?.recentPhotoUrl ? (
            <img src={preview.recentPhotoUrl} alt="" aria-hidden="true" className={styles['banner-photo']} />
          ) : (
            <div className={styles['banner-placeholder']} aria-hidden="true" />
          )}
          <div className={styles['banner-scrim']} />
          <span className={styles['invite-pill']}>Invitation</span>
          <div className={styles['mascot']}>
            <MachhaCharacter state="Onboarding" part="body" size={104} />
          </div>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className={styles['body']}>
          <div className={styles['inviter-row']}>
            <Avatar name={data.inviterName} src={data.inviterPicture} size="sm" />
            <div className={styles['inviter-text']}>
              <span className={styles['inviter-name']}>{data.inviterName}</span>
              <span className={styles['inviter-caption']}>invited you to manage this event</span>
            </div>
          </div>

          <h2 className={styles['event-name']}>{eventName}</h2>

          {preview ? (
            <div className={styles['meta']}>
              <span className={styles['meta-item']}>
                <Calendar size={12} aria-hidden="true" /> {formatDate(preview.createdAt)}
              </span>
              <span className={styles['meta-item']}>
                <Images size={12} aria-hidden="true" /> {preview.photoCount.toLocaleString()} photos
              </span>
              <span className={styles['meta-item']}>
                <BookImage size={12} aria-hidden="true" /> {preview.albumCount} albums
              </span>
            </div>
          ) : previewLoading ? (
            <div className={styles['meta-skeleton']} aria-hidden="true" />
          ) : null}

          {preview?.description ? (
            <p className={styles['description']}>{preview.description}</p>
          ) : null}

          {/* Permissions summary */}
          <div className={styles['perms']}>
            <span className={styles['perms-heading']}>You&apos;ll be able to</span>
            {isFull ? (
              <span className={styles['perms-full']}>Manage everything in this event</span>
            ) : granted.length === 0 ? (
              <span className={styles['perms-full']}>View this event (no edit permissions)</span>
            ) : (
              <ul className={styles['perms-list']}>
                {visible.map((p) => (
                  <li key={p} className={styles['perms-item']}>{p}</li>
                ))}
                {overflow > 0 && <li className={styles['perms-overflow']}>+{overflow} more</li>}
              </ul>
            )}
          </div>

          {/* Actions */}
          <div className={styles['actions']}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDecline}
              disabled={busy}
              loading={actioning === 'decline'}
            >
              <X size={14} style={{ marginRight: '0.25rem' }} />
              Decline
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onAccept}
              disabled={busy}
              loading={actioning === 'accept'}
            >
              <Check size={14} style={{ marginRight: '0.25rem' }} />
              Accept invite
            </Button>
          </div>

          <button type="button" className={styles['skip-btn']} onClick={onSkip} disabled={busy}>
            Skip for now
          </button>
        </div>
      </div>
    </Modal>
  );
}
